import { GetServerSideProps } from "next";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import { Address } from "viem";
import { useTranslation } from "next-i18next";
import { dehydrate, QueryClient } from "@tanstack/react-query";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { PageDataFetchError, useCurrentChainInfo } from "@looksrare/utils";
import { getCanonicalAndLanguageAlternates } from "config/seo";
import { headers } from "config/server";
import { getWatchlistBase } from "utils/graphql/watchlist";
import { SingleWatchlistView } from "views/watchlists/SingleWatchlistView";
import Page from "components/Layout/Page";
import { watchlistKeys } from "hooks/watchlists";

const SingleWatchlistPage = ({ watchlistId, ownerAddress }: { watchlistId: number; ownerAddress: Address }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const currentChainInfo = useCurrentChainInfo();

  return (
    <Page>
      <NextSeo
        {...getCanonicalAndLanguageAlternates(`/watchlists/${watchlistId}`, router, currentChainInfo.appUrl)}
        noindex
        nofollow
        title={t("{{pageTitle}} | LooksRare", {
          pageTitle: t("View {{address}}'s Watchlist", { address: ownerAddress }),
        })}
        description={t(
          "Find out what collections other users have been adding to their watchlists - only on LooksRare."
        )}
        openGraph={{
          title: t("{{pageTitle}} | LooksRare", {
            pageTitle: t("View {{address}}'s Watchlist", { address: ownerAddress }),
          }),
          description: t(
            "Find out what collections other users have been adding to their watchlists - only on LooksRare."
          ),
          url: `${currentChainInfo.appUrl}/watchlists/${watchlistId}`,
          images: [
            {
              url: `${currentChainInfo.appUrl}/images/view-my-watchlist.png`,
              width: 1600,
              height: 900,
              alt: "Watchlist",
            },
          ],
        }}
      />
      <SingleWatchlistView watchlistId={watchlistId} />
    </Page>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ locale, params, res }) => {
  try {
    const queryClient = new QueryClient();

    const { watchlistId: watchlistIdString } = params || {};
    const watchlistId = Number(watchlistIdString);

    // @NOTE this is refetched client-side if we have the user's address
    const watchlist = await getWatchlistBase({ watchlistId }, headers);

    if (!watchlist) {
      return {
        notFound: true,
      };
    }

    queryClient.setQueryData([...watchlistKeys.single(), watchlistId], () => watchlist);
    res.setHeader("Cache-Control", "public, max-age=5, s-maxage=10, stale-while-revalidate=30");
    return {
      props: {
        ...(await serverSideTranslations(locale!, ["common"])),
        watchlistId,
        ownerAddress: watchlist.owner.address,
        locale,
        // Known issue with serializing undefined pageParams[0] in infiniteQuery prefetch https://github.com/tannerlinsley/react-query/issues/1458
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
      },
    };
  } catch (e) {
    throw new PageDataFetchError(`/watchlists/${params?.watchlistId || "[watchlistId]"}`, e);
  }
};

export default SingleWatchlistPage;
